import { useCallback, useContext, useState } from 'react';
import InviteUserForm from './InviteUsersForm';
import ChallengeContext from '../../../../../../Challenge/ChallengeView/ChallengeContext';
import useApi from '@/lib/api/useApi';
import ChallengePortfolioContext from '@/components/ChallengePortfolio/ChallengePortfolioContext';
import NewlySharedContext from '@/components/ChallengePortfolio/NormalView/NewlySharedChallenges/NewlySharedContext';
import SharingErrorDialog from './SharingErrorDialog';
import {
  ChallengeSharingResult,
  type IndividualUserInviteSuccessResponse,
  type IndividualUserInviteErrorResponse,
  type UserInviteResponse,
} from './types';

export const MAX_INVITEES_PER_REQUEST = 5;

// TODO In the future (after late 2026), the handling for faultily attached
// users could removed.
const InviteUsers = () => {
  const api = useApi();

  const [isNewlyShared, , setNewlySharedChallenges] =
    useContext(NewlySharedContext);
  const [, setChallengePortfolio] = useContext(ChallengePortfolioContext);
  const [challenge, setChallenge] = useContext(ChallengeContext);

  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [successfulUserInvitations, setSuccessfulUserInvitations] = useState<
    [string, IndividualUserInviteSuccessResponse][] | null
  >(null);
  const [failedUserInvitations, setFailedUserInvitations] = useState<
    [string, IndividualUserInviteErrorResponse][] | null
  >(null);

  const inviteUsers = useCallback(
    async (emails: string[]): Promise<void> => {
      if (emails.length === 0 || emails.length > MAX_INVITEES_PER_REQUEST) {
        return;
      }

      setSuccessfulUserInvitations(null);
      setFailedUserInvitations(null);

      const userInviteResponse = await api.post<UserInviteResponse>(
        `challenge-portfolio/challenges/${challenge.id}/users/invite-multiple`,
        {
          emails,
        },
      );

      const allChallengeUserEmails = [
        ...challenge.users.map(({ email }) => email),
        ...challenge.invitees.map(({ email }) => email),
      ];

      const invitationResultsArray = Object.entries(userInviteResponse);

      const successfulInvitations = invitationResultsArray.filter(
        ([, { challengeSharingResult }]) =>
          challengeSharingResult === ChallengeSharingResult.SUCCESS,
      ) as [string, IndividualUserInviteSuccessResponse][];
      const failedInvitations = invitationResultsArray.filter(
        ([, { challengeSharingResult }]) =>
          challengeSharingResult !== ChallengeSharingResult.SUCCESS,
      ) as [string, IndividualUserInviteErrorResponse][];

      for (const [, { user }] of successfulInvitations) {
        const userWasAlreadyFaultilyAttached = allChallengeUserEmails.includes(
          user.email,
        );

        if (userWasAlreadyFaultilyAttached) {
          return;
        }

        setChallenge(challenge => {
          challenge.usersCount++;
          challenge.invitees.push(user);
        });

        if (isNewlyShared) {
          setNewlySharedChallenges(newlySharedChallenges => {
            newlySharedChallenges[challenge.id].invitees.push(user);
          });
        } else {
          setChallengePortfolio(challengePortfolio => {
            challengePortfolio.challenges[challenge.id].invitees.push(user);
          });
        }
      }

      setSuccessfulUserInvitations(successfulInvitations);
      setFailedUserInvitations(failedInvitations);

      if (failedInvitations.length > 0) {
        setShowErrorDialog(true);
      }
    },
    [
      api,
      isNewlyShared,
      challenge,
      setChallenge,
      setChallengePortfolio,
      setNewlySharedChallenges,
      setSuccessfulUserInvitations,
      setFailedUserInvitations,
      setShowErrorDialog,
    ],
  );

  return (
    <>
      <InviteUserForm onInvite={inviteUsers} />
      <SharingErrorDialog
        open={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        successfulUserInvitations={successfulUserInvitations}
        failedUserInvitations={failedUserInvitations}
      />
    </>
  );
};

export default InviteUsers;
